import { Duration, Effect, Schedule, Stream } from 'effect';

import { TOKEN_REFRESH_INTERVAL_SECONDS } from '@vs-shop/engine/config/tokenRefresh';

import { isLoggedInAtom } from './auth';
import { refreshTokenFlow } from '../../flow/auth/refreshTokenFlow';
import { RuntimeClientAtom } from '@vs-shop/engine/runtime/RuntimeClient';
import * as Atom from 'effect/unstable/reactivity/Atom';

// v4: `Stream.repeatEffectWithSchedule(effect, schedule)` is now
// `Stream.fromEffect(effect).pipe(Stream.repeat(schedule))`. `Schedule.jittered`
// takes no options (always 80–120% range). `Schedule.compose` was removed; use
// `Schedule.andThen` (or wrap with `Schedule.recurs` inline). `Stream.mapAccum`
// takes a `LazyArg<S>` initial state and the reducer returns
// `[state, ReadonlyArray<value>]`.
const refreshStream = Stream.fromEffect(
  Effect.gen(function* () {
    const now = yield* Effect.sync(() => Date.now());
    const result = yield* refreshTokenFlow;
    return { result, timestamp: now };
  })
).pipe(
  Stream.repeat(
    Schedule.spaced(Duration.seconds(TOKEN_REFRESH_INTERVAL_SECONDS)).pipe(
      Schedule.jittered,
      Schedule.tap(({ output: iteration }) =>
        Effect.logDebug('Schedule iteration').pipe(
          Effect.annotateLogs({ iteration, component: 'tokenRefresh' })
        )
      )
    )
  ),
  Stream.mapAccum(
    () => ({ count: 0, lastTimestamp: 0 }),
    (acc, current) => {
      const timeSinceLastMs = acc.lastTimestamp === 0 ? 0 : current.timestamp - acc.lastTimestamp;
      return [
        { count: acc.count + 1, lastTimestamp: current.timestamp },
        [
          {
            count: acc.count + 1,
            actualDelayMs: timeSinceLastMs,
            actualDelaySeconds: timeSinceLastMs / 1000,
          },
        ] as const,
      ] as const;
    }
  ),
  Stream.tap((info) =>
    info.count > 1
      ? Effect.logDebug('Actual delay measured').pipe(
          Effect.annotateLogs({
            iteration: info.count - 1,
            actualDelayMs: info.actualDelayMs,
            actualDelaySeconds: info.actualDelaySeconds,
            component: 'tokenRefresh',
          })
        )
      : Effect.void
  ),
  Stream.map((info) => info.count),
  Stream.tapError((error) => Effect.logError('[tokenRefreshAtom] Refresh failed, retrying', error)),
  Stream.retry(Schedule.exponential(Duration.seconds(10)).pipe(Schedule.concat(Schedule.recurs(5))))
);

export const tokenRefreshAtom = RuntimeClientAtom.atom((get) => {
  const isLoggedIn = get(isLoggedInAtom);

  if (!isLoggedIn) {
    return Stream.empty as typeof refreshStream;
  }

  return refreshStream;
}).pipe(Atom.keepAlive);
